// Ajouter ces modifications au RegisterForm existant
import React, { useState, useEffect } from 'react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { CheckCircle } from 'lucide-react';

interface RegisterFormProps {
  onSubmit: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    institutionId?: string;
  }) => Promise<void>;
  isLoading?: boolean;
  userType: 'secretary' | 'supervisor';
}

const RegisterForm: React.FC<RegisterFormProps> = ({ 
  onSubmit, 
  isLoading = false,
  userType 
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    institutionId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Calculer la force du mot de passe
  useEffect(() => {
    if (!formData.password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    
    // Longueur minimum
    if (formData.password.length >= 6) strength += 20;
    if (formData.password.length >= 8) strength += 20;
    
    // Contient des majuscules
    if (/[A-Z]/.test(formData.password)) strength += 20;
    
    // Contient des chiffres
    if (/\d/.test(formData.password)) strength += 20;
    
    // Contient des caractères spéciaux
    if (/[^A-Za-z0-9]/.test(formData.password)) strength += 20;
    
    setPasswordStrength(strength);
  }, [formData.password]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, formData[name as keyof typeof formData]);
  };

  const validateField = (fieldName: string, value: string) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'email':
        if (!value.trim()) {
          newErrors.email = 'Email requis';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          newErrors.email = 'Email invalide';
        } else {
          delete newErrors.email;
        }
        break;
        
      case 'password':
        if (!value) {
          newErrors.password = 'Mot de passe requis';
        } else if (value.length < 6) {
          newErrors.password = 'Minimum 6 caractères';
        } else {
          delete newErrors.password;
        }
        break;
        
      case 'confirmPassword':
        if (value !== formData.password) {
          newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
        } else {
          delete newErrors.confirmPassword;
        }
        break;
        
      case 'firstName':
        if (!value.trim()) {
          newErrors.firstName = 'Prénom requis';
        } else {
          delete newErrors.firstName;
        }
        break;
        
      case 'lastName':
        if (!value.trim()) {
          newErrors.lastName = 'Nom requis';
        } else {
          delete newErrors.lastName;
        }
        break;
        
      case 'institutionId':
        if (userType === 'secretary' && !value.trim()) {
          newErrors.institutionId = 'Identifiant d\'institution requis';
        } else {
          delete newErrors.institutionId;
        }
        break;
        
      case 'phone':
        if (value && !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(value)) {
          newErrors.phone = 'Numéro de téléphone invalide';
        } else {
          delete newErrors.phone;
        }
        break;
    }
    
    setErrors(newErrors);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) newErrors.email = 'Email requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.password) newErrors.password = 'Mot de passe requis';
    else if (formData.password.length < 6) {
      newErrors.password = 'Minimum 6 caractères';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.firstName.trim()) newErrors.firstName = 'Prénom requis';
    if (!formData.lastName.trim()) newErrors.lastName = 'Nom requis';

    if (userType === 'secretary' && !formData.institutionId.trim()) {
      newErrors.institutionId = 'Identifiant d\'institution requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Marquer tous les champs comme touchés
    Object.keys(formData).forEach(key => {
      setTouched(prev => ({ ...prev, [key]: true }));
    });
    
    if (!validateForm()) {
      return;
    }

    const { confirmPassword, ...submitData } = formData;
    await onSubmit({
      ...submitData,
      institutionId: submitData.institutionId || undefined,
      phone: submitData.phone || undefined,
    });
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 40) return 'bg-red-500';
    if (passwordStrength <= 60) return 'bg-yellow-500';
    if (passwordStrength <= 80) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 40) return 'Faible';
    if (passwordStrength <= 60) return 'Moyen';
    if (passwordStrength <= 80) return 'Bon';
    return 'Excellent';
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Input
            label="Prénom"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isLoading}
            error={errors.firstName}
            autoComplete="given-name"
          />
        </div>
        <div>
          <Input
            label="Nom"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isLoading}
            error={errors.lastName}
            autoComplete="family-name"
          />
        </div>
      </div>

      <div>
        <Input
          label="Adresse email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          disabled={isLoading}
          error={errors.email}
          placeholder="exemple@institution.com"
          autoComplete="email"
        />
      </div>

      <div>
        <Input
          label="Numéro de téléphone"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isLoading}
          error={errors.phone}
          placeholder="+225 01 23 45 67 89"
          autoComplete="tel"
        />
      </div>

      {userType === 'secretary' && (
        <div>
          <Input
            label="Identifiant d'institution"
            name="institutionId"
            value={formData.institutionId}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isLoading}
            error={errors.institutionId}
            placeholder="ID fourni par votre superviseur"
            autoComplete="organization"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Input
            label="Mot de passe"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isLoading}
            error={errors.password}
            autoComplete="new-password"
          />
          {formData.password && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Force du mot de passe</span>
                <span className={`font-medium ${
                  passwordStrength <= 40 ? 'text-red-600' :
                  passwordStrength <= 60 ? 'text-yellow-600' :
                  passwordStrength <= 80 ? 'text-blue-600' : 'text-green-600'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                  style={{ width: `${passwordStrength}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500 space-y-1">
                {formData.password.length < 8 && (
                  <p>✓ Minimum 8 caractères recommandés</p>
                )}
                {!/[A-Z]/.test(formData.password) && (
                  <p>✓ Ajouter une majuscule</p>
                )}
                {!/\d/.test(formData.password) && (
                  <p>✓ Ajouter un chiffre</p>
                )}
                {!/[^A-Za-z0-9]/.test(formData.password) && (
                  <p>✓ Ajouter un caractère spécial</p>
                )}
              </div>
            </div>
          )}
        </div>
        <div>
          <Input
            label="Confirmer mot de passe"
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isLoading}
            error={errors.confirmPassword}
            autoComplete="new-password"
          />
          {formData.confirmPassword && formData.password === formData.confirmPassword && (
            <div className="mt-2 flex items-center text-sm text-green-600">
              <CheckCircle className="h-4 w-4 mr-1" />
              Les mots de passe correspondent
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start pt-2">
        <input
          id="terms"
          name="terms"
          type="checkbox"
          required
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
          disabled={isLoading}
        />
        <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
          J'accepte les{' '}
          <a href="/terms" className="text-blue-600 hover:text-blue-500 font-medium">
            conditions d'utilisation
          </a>{' '}
          et la{' '}
          <a href="/privacy" className="text-blue-600 hover:text-blue-500 font-medium">
            politique de confidentialité
          </a>
        </label>
      </div>

      <div className="pt-4">
        <Button
          type="submit"
          variant="primary"
          className="w-full py-3 text-base font-medium shadow-md hover:shadow-lg transition-shadow"
          isLoading={isLoading}
          disabled={isLoading}
        >
          Créer mon compte
        </Button>
      </div>
    </form>
  );
};

export default RegisterForm;